import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatDateTime, formatMoney } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import {
  editProposal,
  getProposal,
  proposalDocument,
  proposalTone,
  sendProposal,
  withdrawProposal,
  type ProposalDetail,
} from './api';

/**
 * One proposal.
 *
 * The link is shown once, right after sending, because the token behind it is stored only as a
 * hash and cannot be recovered. That is said plainly on screen: a rep who closes this panel without
 * copying the link sends again and gets a new one, which is the honest recovery.
 */
export function ProposalPage(): React.JSX.Element {
  const { proposalId = '' } = useParams();

  const proposal = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () => getProposal(proposalId),
    enabled: proposalId !== '',
  });

  if (proposal.isLoading) return <Loading />;
  if (proposal.error)
    return <ErrorState error={proposal.error} onRetry={() => void proposal.refetch()} />;
  if (!proposal.data) return <Loading />;

  return <Detail proposal={proposal.data} />;
}

function Detail({ proposal }: { proposal: ProposalDetail }): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['proposal', proposal.id] });
    void client.invalidateQueries({ queryKey: ['proposals'] });
  };

  const send = useMutation({
    mutationFn: () => sendProposal(proposal.id),
    onSuccess: (sent) => {
      setError(null);
      setLink(sent.publicUrl ?? null);
      setCopied(false);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const withdraw = useMutation({
    mutationFn: () => withdrawProposal(proposal.id),
    onSuccess: () => {
      setError(null);
      setLink(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const document = useMutation({
    mutationFn: () => proposalDocument(proposal.id),
    onSuccess: ({ url }) => window.open(url, '_blank', 'noopener'),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const open = proposal.status === 'SENT' || proposal.status === 'VIEWED';

  return (
    <div className="view view--wide">
      <PageHeader
        title={`${t('proposals.one')} ${proposal.number} · v${proposal.version}`}
        description={proposal.title}
        actions={
          <div className="cell-inline">
            <Link
              className="button button--quiet button--compact"
              to={`/crm/negocios/${proposal.opportunityId}`}
            >
              {t('proposals.openDeal')}
            </Link>

            {proposal.documentSha256 && (
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={document.isPending}
                onClick={() => document.mutate()}
              >
                {t('proposals.openPdf')}
              </button>
            )}

            {(proposal.status === 'DRAFT' || open) && (
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={send.isPending}
                onClick={() => send.mutate()}
              >
                {proposal.status === 'DRAFT' ? t('proposals.send') : t('proposals.resend')}
              </button>
            )}

            {(proposal.status === 'DRAFT' || open) && (
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={withdraw.isPending}
                onClick={() => withdraw.mutate()}
              >
                {t('proposals.withdraw')}
              </button>
            )}
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {link && (
        <Panel title={t('proposals.linkReady')}>
          <p className="muted small">{t('proposals.linkOnce')}</p>
          <div className="cell-inline">
            <code className="proposal__link">{link}</code>
            <button
              type="button"
              className="button button--compact"
              onClick={() => {
                void navigator.clipboard?.writeText(link).then(() => setCopied(true));
              }}
            >
              {copied ? t('proposals.copied') : t('proposals.copy')}
            </button>
          </div>
        </Panel>
      )}

      <div className="ticket">
        <div className="ticket__thread">
          <Panel title={t('proposals.lines')}>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('deal.item')}</th>
                    <th className="numeric">{t('deal.quantity')}</th>
                    <th className="numeric">{t('proposals.unit')}</th>
                    <th className="numeric">{t('proposals.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.description}
                        {item.recurring && <Badge tone="info">{t('proposals.monthly')}</Badge>}
                        {item.discountBps > 0 && (
                          <span className="muted small">{` −${(item.discountBps / 100).toFixed(0)}%`}</span>
                        )}
                      </td>
                      <td className="numeric">{item.quantity}</td>
                      <td className="numeric">{formatMoney(item.unitPriceCents)}</td>
                      <td className="numeric">{formatMoney(item.totalCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <dl className="facts">
              {proposal.recurringCents > 0 && (
                <>
                  <dt>{t('proposals.recurring')}</dt>
                  <dd>{formatMoney(proposal.recurringCents)}</dd>
                </>
              )}
              {proposal.oneOffCents > 0 && (
                <>
                  <dt>{t('proposals.oneOff')}</dt>
                  <dd>{formatMoney(proposal.oneOffCents)}</dd>
                </>
              )}
              <dt className="strong">{t('proposals.total')}</dt>
              <dd className="strong">{formatMoney(proposal.totalCents)}</dd>
            </dl>
          </Panel>

          {proposal.status === 'DRAFT' && <DraftEditor proposal={proposal} onSaved={refresh} />}

          {proposal.acceptance && (
            <Panel title={t('proposals.acceptedBy')}>
              <dl className="facts">
                <dt>{t('proposals.who')}</dt>
                <dd>{`${proposal.acceptance.name} · ${proposal.acceptance.email}`}</dd>
                <dt>{t('proposals.when')}</dt>
                <dd>{formatDateTime(proposal.acceptance.acceptedAt)}</dd>
                <dt>{t('proposals.from')}</dt>
                <dd>{proposal.acceptance.ip ?? '—'}</dd>
                <dt>{t('proposals.document')}</dt>
                <dd>
                  <code className="small">{proposal.acceptance.documentSha256.slice(0, 16)}…</code>
                </dd>
              </dl>
            </Panel>
          )}

          {proposal.declineReason && (
            <Panel title={t('proposals.declinedBecause')}>
              <p>{proposal.declineReason}</p>
            </Panel>
          )}
        </div>

        <aside className="ticket__side">
          <Panel title={t('crm.field.status')}>
            <Badge tone={proposalTone(proposal.status)}>
              {t(`proposals.status.${proposal.status}`)}
            </Badge>

            <dl className="facts">
              <dt>{t('proposals.term')}</dt>
              <dd>
                {proposal.termMonths > 0
                  ? t('proposals.months').replace('{n}', String(proposal.termMonths))
                  : t('proposals.noTerm')}
              </dd>
              <dt>{t('proposals.validity')}</dt>
              <dd>{t('proposals.days').replace('{n}', String(proposal.validityDays))}</dd>
              {proposal.sentAt && (
                <>
                  <dt>{t('proposals.sentAt')}</dt>
                  <dd>{formatDate(proposal.sentAt)}</dd>
                </>
              )}
              {proposal.expiresAt && (
                <>
                  <dt>{t('proposals.validUntil')}</dt>
                  <dd>{formatDate(proposal.expiresAt)}</dd>
                </>
              )}
              {proposal.viewedAt && (
                <>
                  <dt>{t('proposals.viewedAt')}</dt>
                  <dd>{formatDateTime(proposal.viewedAt)}</dd>
                </>
              )}
            </dl>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

/** Text a draft carries beyond its lines. Editable only while it is a draft; after that, a new version. */
function DraftEditor({
  proposal,
  onSaved,
}: {
  proposal: ProposalDetail;
  onSaved: () => void;
}): React.JSX.Element {
  const [title, setTitle] = useState(proposal.title);
  const [introduction, setIntroduction] = useState(proposal.introduction ?? '');
  const [terms, setTerms] = useState(proposal.terms ?? '');
  const [termMonths, setTermMonths] = useState(String(proposal.termMonths));
  const [validityDays, setValidityDays] = useState(String(proposal.validityDays));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      editProposal(proposal.id, {
        title: title.trim(),
        introduction: introduction.trim() || null,
        terms: terms.trim() || null,
        termMonths: Number(termMonths),
        validityDays: Number(validityDays),
      }),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Panel title={t('proposals.text')}>
      <label className="stack__field">
        <span>{t('proposals.title')}</span>
        <input value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} />
      </label>

      <label className="stack__field">
        <span>{t('proposals.introduction')}</span>
        <textarea
          rows={4}
          value={introduction}
          maxLength={5000}
          onChange={(event) => setIntroduction(event.target.value)}
        />
      </label>

      <label className="stack__field">
        <span>{t('proposals.terms')}</span>
        <textarea
          rows={6}
          value={terms}
          maxLength={20_000}
          onChange={(event) => setTerms(event.target.value)}
        />
      </label>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('proposals.termMonths')}</span>
          <input
            type="number"
            min={0}
            max={120}
            value={termMonths}
            onChange={(event) => setTermMonths(event.target.value)}
          />
        </label>
        <label className="stack__field">
          <span>{t('proposals.validityDays')}</span>
          <input
            type="number"
            min={1}
            max={365}
            value={validityDays}
            onChange={(event) => setValidityDays(event.target.value)}
          />
        </label>
      </div>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      <div className="confirm__actions">
        <button
          type="button"
          className="button button--compact"
          disabled={title.trim() === '' || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('proposals.saveDraft')}
        </button>
      </div>
    </Panel>
  );
}
